import { useRef, useState, useEffect } from 'react'

import { useEventListener } from 'hooks'
import { useOnClickOutside } from 'hooks/useOnClickOutside'

import { getIsTouchDevice } from 'utils'

import debounce from 'lodash.debounce'

import { MOBILE_TOOLTIP_AUTO_CLOSE_TIME } from '../consts'
import { TBtnElement, TScrollableParentRefType } from '../types'

export const useTooltipVisibility = ({
  btnElement,
  scrollableParentRef,
  showMobileTooltip,
  type,
  isToggledByClick = false
}: {
  btnElement: TBtnElement
  scrollableParentRef?: TScrollableParentRefType
  showMobileTooltip: boolean
  type: 'default' | 'floating-x-top'
  isToggledByClick?: boolean
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const mobileTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isTouchDevice = getIsTouchDevice()

  const clearTimers = () => {
    if (mobileTimeoutRef.current) {
      clearTimeout(mobileTimeoutRef.current)
    }
  }
  const handleMouseHover = () => {
    if (isTouchDevice || isToggledByClick) return
    setIsVisible(true)
  }

  const handleMouseLeave = () => {
    if (isTouchDevice || isToggledByClick) return
    setIsVisible(false)
  }

  const handleClickOrTouch = () => {
    if (isVisible) {
      clearTimers()
      setIsVisible(false)
    } else {
      setIsVisible(true)
      mobileTimeoutRef.current = setTimeout(() => {
        setIsVisible(false)
      }, MOBILE_TOOLTIP_AUTO_CLOSE_TIME)
    }
  }

  const handleWebClick = () => {
    if (isToggledByClick) {
      handleClickOrTouch()
      return
    }
    return
  }

  const handleMobileTouch = () => {
    if (isTouchDevice && !showMobileTooltip) return
    handleClickOrTouch()
  }

  const handleFloatingTooltipScroll = () => {
    debounce(() => {
      setIsVisible(true)
    }, 500)
  }

  const resetTooltip = () => {
    if (isVisible) {
      clearTimers()
      setIsVisible(false)
    }
  }

  const handleScroll = () => {
    resetTooltip()
    if (type === 'floating-x-top') {
      handleFloatingTooltipScroll()
    }
  }

  useEventListener('click', handleWebClick, btnElement)
  useEventListener('touchstart', handleMobileTouch, btnElement)
  useEventListener('mouseenter', handleMouseHover, btnElement)
  useEventListener('mouseleave', handleMouseLeave, btnElement)
  useEventListener('scroll', handleScroll, scrollableParentRef?.current)

  useOnClickOutside(btnElement ? [btnElement] : null, resetTooltip)

  useEffect(() => {
    return () => {
      clearTimers()
    }
  }, [])

  return isVisible
}
